import React from "react";

/**
 * Photography-first editorial hero with a permanent typography governance system.
 *
 * Imagery rules:
 *   - Pass `image` (string) for a single-image hero (default).
 *   - Pass `images` (array of 2–4 URLs) for a unified four-zone composition
 *     used on /projects to communicate portfolio stewardship.
 *
 * Readability rules (always-on):
 *   - Layer 1 (uniform): navy tone at 40–70% strength to unify any photograph.
 *     Tunable per page via `overlayTone` ("balanced" | "soft" | "strong").
 *   - Layer 2 (protected typography zone): bottom-anchored gradient that always
 *     guarantees WCAG AA contrast under the headline / sub-text / CTA cluster.
 *   - Layer 3 (typography softening): a 2px backdrop-blur applied behind the
 *     text block to further soften any visually busy imagery directly under
 *     the headline.
 *
 * The Evergreen brand system always takes precedence over photography.
 */

const TONE_PRESETS = {
  // Uniform navy layer strength
  soft:      { base: "bg-[#0A1F44]/40" },
  balanced:  { base: "bg-[#0A1F44]/50" },
  strong:    { base: "bg-[#0A1F44]/60" },
};

export default function PageHeader({
  eyebrow,
  title,
  intro,
  image,
  images,
  testId = "page-header",
  meta,
  overlayTone = "balanced",
}) {
  const hasMosaic = Array.isArray(images) && images.length >= 2;
  const tone = TONE_PRESETS[overlayTone] || TONE_PRESETS.balanced;

  return (
    <section data-testid={testId} className="relative bg-[#0A1F44] text-white overflow-hidden">
      {/* Image layer */}
      {hasMosaic ? (
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2" aria-hidden="true">
          {images.slice(0, 4).map((src, i) => (
            <div
              key={src + i}
              className="bg-cover bg-center"
              style={{ backgroundImage: `url(${src})` }}
            />
          ))}
        </div>
      ) : image ? (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${image})` }}
          aria-hidden="true"
        />
      ) : (
        <div className="absolute inset-0 bg-[#0A1F44]" aria-hidden="true" />
      )}

      {/* Layer 1 — uniform navy tone (40–70% per brief) */}
      {(image || hasMosaic) && (
        <div className={`absolute inset-0 ${tone.base}`} aria-hidden="true" />
      )}

      {/* Layer 2 — protected typography zone: bottom-anchored navy gradient.
          Guarantees WCAG AA contrast for white serif headlines + sub-text. */}
      {(image || hasMosaic) && (
        <div
          className="absolute inset-x-0 bottom-0 h-[78%] bg-gradient-to-t from-[#0A1F44]/95 via-[#0A1F44]/70 to-transparent"
          aria-hidden="true"
        />
      )}

      {/* Content with Layer 3 — soft blur behind the typography block */}
      <div className="relative evg-container min-h-[70vh] md:min-h-[78vh] flex flex-col justify-end pt-32 pb-20 md:pb-28">
        <div
          className="max-w-5xl backdrop-blur-[2px] -mx-2 px-2 py-2"
          data-reveal
        >
          {eyebrow ? (
            <div className="evg-overline-gold mb-8" data-testid={`${testId}-eyebrow`}>
              {eyebrow}
            </div>
          ) : null}

          <h1 className="evg-display-xl text-white" data-testid={`${testId}-title`}>
            {title}
          </h1>

          {intro ? (
            <p
              className="evg-lead evg-lead-light mt-10 max-w-2xl"
              data-testid={`${testId}-intro`}
            >
              {intro}
            </p>
          ) : null}

          {meta ? (
            <div className="mt-12 evg-meta evg-meta-light" data-testid={`${testId}-meta`}>
              {meta}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
